import Color from 'color';
import { hasCategory } from 'shared/utils/categories';
import { GRADE, CSI, NPS, FIND, COLOR } from 'shared/constants/questionCategories';
import { GRADE_COLORS, CHART_COLORS, getColorMix } from 'shared/constants/colors';
import { round, average, fraction } from 'shared/constants/math';
import { QUESTION_TYPES } from 'shared/constants/survey';
import { axisFormatter, formatSegment } from './formatters';
import messages from './messages';

export const addColors = (vals, picker) => vals && vals.map((v, i) => ({
  ...v,
  itemStyle: {
    color: picker(v, i),
  },
}));

export const iconBase = {
  width: 25,
  height: 14,
  padding: 0,
  borderRadius: 4,
  verticalAlign: 'top',
};

export const mergeValues = (segment, question, formatMessage) => {
  const formatValue = optionValue => formatMessage && messages[optionValue] && formatMessage(messages[optionValue]);
  const { categories, type } = question;

  const { values } = segment;
  let messagePrefix = '';

  if (hasCategory(categories, [CSI])) {
    messagePrefix = 'csi';
  }

  if (type === QUESTION_TYPES.NPS || hasCategory(categories, [NPS])) {
    messagePrefix = 'nps';
  }

  if (messagePrefix.length > 0) {
    const getOptionValue = v => (v?.optionValue && messagePrefix === 'csi' ? Math.round(v.optionValue / 2) : v.optionValue);
    return values.reduce((arr, v) => {
      if (v.optionValue == null) {
        arr.push(v);
      } else {
        const optionValue = getOptionValue(v);
        const val = arr.find(x => x.optionValue === optionValue);

        if (val) {
          val.value += v.value;
          val.data = val.data.map((d, i) => d + v.data[i]);
          val.optionIds.push(v.id);
        } else {
          arr.push({
            ...v,
            optionIds: [v.id],
            optionValue,
            name: formatValue(`${messagePrefix}OptionValue_${optionValue}`) || v.name,
          });
        }
      }
      return arr;
    }, []);
  }

  return values;
};

export const colorChartParts = (question, values, colorByIndex) => {
  let newValues = values;

  const { categories, type } = question;

  if ([QUESTION_TYPES.NPS, QUESTION_TYPES.FIND, QUESTION_TYPES.GRADE].includes(type) || hasCategory(categories, [FIND, GRADE, NPS, CSI, COLOR.BY_VALUE])) {
    let nulls = 0;
    const optionValues = values.filter(v => v.optionValue != null).map(v => v.optionValue);
    const avg = average([...new Set(optionValues)]);

    const optionValuesBelowAvg = optionValues.filter(v => v < avg);
    const minBelowAvg = Math.min(...optionValuesBelowAvg);
    const maxBelowAvg = Math.max(...optionValuesBelowAvg);
    const optionValuesAboveAvg = optionValues.filter(v => v > avg);
    const minAboveAvg = Math.min(...optionValuesAboveAvg);
    const maxAboveAvg = Math.max(...optionValuesAboveAvg);

    newValues = addColors(values, ({ optionValue }) => {
      if (optionValue == null) {
        return CHART_COLORS[nulls++ % CHART_COLORS.length];
      }

      if (optionValue < avg) {
        if (optionValue === minBelowAvg) {
          return GRADE_COLORS['1'];
        }
        return getColorMix(Color, GRADE_COLORS['1'], GRADE_COLORS['2'], optionValue, minBelowAvg, maxBelowAvg);
      }

      if (optionValue > avg) {
        if (optionValue === maxAboveAvg) {
          return GRADE_COLORS['5'];
        }
        return getColorMix(Color, GRADE_COLORS['4'], GRADE_COLORS['5'], optionValue, minAboveAvg, maxAboveAvg);
      }

      return GRADE_COLORS['3'];
    });
  }

  if (colorByIndex || hasCategory(categories, [COLOR.BY_INDEX])) {
    let nulls = 0;

    newValues = addColors(values, (v, i) => {
      if (v.optionValue == null) {
        return CHART_COLORS[nulls++ % CHART_COLORS.length];
      }

      if (i + 1 < values.length / 2) {
        return getColorMix(Color, GRADE_COLORS['1'], GRADE_COLORS['2'], i, 0, (values.length - 1) / 2);
      }

      if (i + 1 > values.length / 2) {
        return getColorMix(Color, GRADE_COLORS['4'], GRADE_COLORS['5'], i, 0, (values.length - 1) / 2);
      }

      return GRADE_COLORS['3'];
    });
  }

  return newValues;
};

export const uniqueSegmentId = segment => `${segment.segmentId} (${segment.dateRange.startDate} - ${segment.dateRange.endDate})`;

export const getSegment = (segments, uniqueId) => segments.find(s => uniqueSegmentId(s) === uniqueId);

export const getGridOptions = () => ({
  left: 20,
  right: 20,
  top: 50,
  bottom: 20,
  containLabel: true,
});

export const getValueAxis = ({ showAsPercentage, adjustToValues, ...props }) => {
  const axis = {
    type: 'value',
    boundaryGap: [0, '5%'],
    axisLabel: {
      formatter: axisFormatter(showAsPercentage),
    },
    ...props,
  };

  if (adjustToValues) {
    const getPrecision = ({ min, max }) => {
      const diff = max - min;
      if (diff < 10) {
        return 0;
      }
      return -1;
    };
    axis.min = (value) => round(value.min, getPrecision(value), { method: Math.floor });
    axis.max = (value) => round(value.max, getPrecision(value), { method: Math.ceil });
  }

  return axis;
};

export const getUniqueValues = (segments) => {
  const allValues = [].concat(...segments.map(segment => segment.values));
  return allValues
    .filter((value, index, arr) => arr
      .map(v => v.name)
      .indexOf(value.name) === index);
};

const gradingScaleSteps = ['scaleNotApproved', 'scaleApproved', 'scaleGood', 'scaleVeryGood'];
const getGradingScaleStep = (scale, step) => `${scale}S${step.substring(1)}`;

export const getGradingScale = (question, chartSettings, formatMessage) => {
  if (!['pie', 'line', 'area'].includes(chartSettings?.type)) {
    return null;
  }

  if (['line', 'area'].includes(chartSettings?.type) && chartSettings?.displayMode !== 'score' && question.type !== 'SCORE') {
    return null;
  }

  let scale;

  if (hasCategory(question.categories || question.scoreCategories, [GRADE])) {
    scale = 'grade';
  }

  if (question.type === QUESTION_TYPES.NPS || hasCategory(question.categories, [NPS])) {
    scale = 'nps';
  }

  if (question.type === QUESTION_TYPES.FIND || hasCategory(question.categories, [FIND])) {
    scale = 'find';
  }

  if (!scale) {
    return null;
  }

  return {
    type: 'group',
    left: 'center',
    bottom: 20,
    children: [
      {
        type: 'text',
        left: 'center',
        top: 0,
        style: {
          text: formatMessage(messages.gradingScale),
          fontWeight: 'bold',
          fontSize: 14,
        },
      },
      {
        type: 'text',
        left: 'center',
        top: 20,
        style: {
          text: gradingScaleSteps.map((step) => `${formatMessage(messages[step])}: ${formatMessage(messages[getGradingScaleStep(scale, step)])}`).join('  |  '),
          fontSize: 14,
        },
      },
    ],
  };
};

export function getExportData({ segments, displayMode, isScoreGraph, formatMessage, locale }) {
  const optionColumn = formatMessage(messages.option);

  const formattedValue = (value, total) => {
    if (displayMode === 'percentage' && !isScoreGraph) {
      return fraction(value, total);
    }
    return value;
  };

  const segmentColumns = segments.map(s => ({
    column: formatSegment(s, { formatMessage, locale, lineBreak: ' ' }),
    segment: s,
  }));

  const columns = [
    optionColumn,
    ...segmentColumns.map(s => s.column),
  ];

  const rows = getUniqueValues(segments).map(v => ({
    [optionColumn]: v.name,
    ...segmentColumns.reduce((obj, s) => {
      const segmentValue = s.segment.values.find(sv => sv.id === v.id);
      if (segmentValue) {
        obj[s.column] = formattedValue(segmentValue.value, s.segment.uniqueCount);
      }
      return obj;
    }, {}),
  }));

  return { columns, rows };
}
